import { useCallback, useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Pencil, Archive, ArchiveRestore, CalendarPlus, FileText, Receipt, ArrowLeft, PawPrint, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PageHeader, Panel, StatusBadge, Loading, EmptyState } from "@/components/common";
import ClientFormDialog from "@/components/ClientFormDialog";
import AppointmentFormDialog from "@/components/AppointmentFormDialog";
import { api, errMsg } from "@/lib/api";
import { money, fmtDateTime, fmtDay, SERVICE_LABELS, METHOD_LABELS } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";

function Info({ label, value, icon: Icon, warn }) {
  if (!value) return null;
  return (
    <div className={`rounded-lg p-3 ${warn ? "bg-amber-50 border border-amber-200" : "bg-slate-50"}`}>
      <div className="text-xs uppercase tracking-wider text-slate-500 flex items-center gap-1">{Icon && <Icon className="h-3.5 w-3.5" />}{label}</div>
      <div className="text-sm text-slate-800 mt-1 whitespace-pre-wrap">{value}</div>
    </div>
  );
}

function Rows({ items, empty, render }) {
  if (!items.length) return <EmptyState title={empty} />;
  return <div className="divide-y divide-slate-100">{items.map(render)}</div>;
}

const rowCls = "flex items-center justify-between gap-3 py-3 hover:bg-slate-50 -mx-2 px-2 rounded-lg";

export default function ClientDetail() {
  const { id } = useParams();
  const { tz } = useAuth();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [hist, setHist] = useState(null);
  const [edit, setEdit] = useState(false);
  const [sched, setSched] = useState(false);

  const load = useCallback(async () => {
    try {
      const [c, h] = await Promise.all([api.get(`/clients/${id}`), api.get(`/clients/${id}/history`)]);
      setClient(c.data); setHist(h.data);
    } catch (e) { toast.error(errMsg(e)); navigate("/clients"); }
  }, [id, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const toggleArchive = async () => {
    try {
      await api.post(`/clients/${id}/archive`, { archived: !client.archived });
      toast.success(client.archived ? "Client restored" : "Client archived");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  if (!client || !hist) return <Loading />;
  const balance = hist.invoices.reduce((s, i) => s + (["void", "draft"].includes(i.payment_status) ? 0 : i.balance), 0);

  return (
    <div data-testid="client-detail-page">
      <Link to="/clients" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900 mb-4" data-testid="back-to-clients"><ArrowLeft className="h-4 w-4" />Clients</Link>
      <PageHeader
        title={client.name}
        subtitle={[client.phone, client.email, client.archived ? "Archived" : null].filter(Boolean).join(" · ")}
        actions={<>
          <Button variant="outline" className="h-11" onClick={() => setEdit(true)} data-testid="edit-client-btn"><Pencil className="h-4 w-4 mr-1" />Edit</Button>
          <Button variant="outline" className="h-11" onClick={toggleArchive} data-testid="archive-client-btn">{client.archived ? <><ArchiveRestore className="h-4 w-4 mr-1" />Restore</> : <><Archive className="h-4 w-4 mr-1" />Archive</>}</Button>
          {!client.archived && <Button className="h-11" onClick={() => setSched(true)} data-testid="client-schedule-btn"><CalendarPlus className="h-4 w-4 mr-1" />Schedule job</Button>}
        </>}
      />
      <div className="flex flex-wrap gap-2 mb-6">
        <Button variant="secondary" asChild className="h-10" data-testid="client-new-estimate-btn"><Link to={`/estimates/new?client_id=${id}`}><FileText className="h-4 w-4 mr-1" />New estimate</Link></Button>
        <Button variant="secondary" asChild className="h-10" data-testid="client-new-invoice-btn"><Link to={`/invoices/new?client_id=${id}`}><Receipt className="h-4 w-4 mr-1" />New invoice</Link></Button>
        <div className="ml-auto text-sm text-slate-600 self-center" data-testid="client-balance">Open balance: <span className="font-semibold text-slate-900">{money(balance)}</span></div>
      </div>
      <Tabs defaultValue="details">
        <TabsList className="mb-4 flex-wrap h-auto">
          {["details", "jobs", "estimates", "invoices", "payments"].map((t) => <TabsTrigger key={t} value={t} className="capitalize h-9" data-testid={`client-tab-${t}`}>{t}</TabsTrigger>)}
        </TabsList>
        <TabsContent value="details">
          <Panel>
            <div className="grid sm:grid-cols-2 gap-3">
              <Info label="Service address" value={client.address} />
              <Info label="Pets" value={client.pets} icon={PawPrint} warn />
              <Info label="Access instructions" value={client.access_instructions} icon={KeyRound} warn />
              <Info label="Property notes" value={client.property_notes} />
              <Info label="Cleaning preferences" value={client.preferences} />
              <Info label="Special requests" value={client.special_requests} />
              <Info label="Recurring preference" value={client.preferred_frequency !== "none" ? [client.preferred_frequency, client.preferred_day, client.preferred_time].filter(Boolean).join(" · ") : ""} />
              <Info label="Message consent" value={`Email: ${client.email_opt_in ? "opted in" : "not opted in"} · SMS: ${client.sms_opt_in ? "opted in" : "not opted in"}`} />
            </div>
            {client.consent_history?.length > 0 && (
              <div className="mt-4 text-xs text-slate-500 space-y-1" data-testid="consent-history">
                {client.consent_history.map((c, i) => <div key={i}>{c.channel.toUpperCase()} {c.opted_in ? "opt-in" : "opt-out"} recorded {new Date(c.at).toLocaleString()} by {c.by}</div>)}
              </div>
            )}
          </Panel>
        </TabsContent>
        <TabsContent value="jobs"><Panel><Rows items={hist.appointments} empty="No jobs yet" render={(a) => (
          <Link key={a.id} to={`/jobs/${a.id}`} className={rowCls} data-testid={`client-job-${a.id}`}><div><div className="font-medium">{fmtDateTime(a.start, tz)}</div><div className="text-xs text-slate-500">{SERVICE_LABELS[a.service_type]} · {money(a.price)}</div></div><StatusBadge status={a.status} /></Link>
        )} /></Panel></TabsContent>
        <TabsContent value="estimates"><Panel><Rows items={hist.estimates} empty="No estimates yet" render={(e) => (
          <Link key={e.id} to={`/estimates/${e.id}`} className={rowCls}><div><div className="font-medium">{e.number}</div><div className="text-xs text-slate-500">{fmtDay(e.created_at)}</div></div><div className="flex items-center gap-3"><span className="font-semibold">{money(e.total)}</span><StatusBadge status={e.status} /></div></Link>
        )} /></Panel></TabsContent>
        <TabsContent value="invoices"><Panel><Rows items={hist.invoices} empty="No invoices yet" render={(i) => (
          <Link key={i.id} to={`/invoices/${i.id}`} className={rowCls}><div><div className="font-medium">{i.number}</div><div className="text-xs text-slate-500">Due {fmtDay(i.due_date)}</div></div><div className="flex items-center gap-3"><span className="font-semibold">{money(i.total)}</span><StatusBadge status={i.payment_status} /></div></Link>
        )} /></Panel></TabsContent>
        <TabsContent value="payments"><Panel><Rows items={hist.payments} empty="No payments recorded" render={(p) => (
          <div key={p.id} className={rowCls}><div><div className="font-medium">{money(p.amount)}</div><div className="text-xs text-slate-500">{fmtDay(p.date)} · {METHOD_LABELS[p.method]} · {p.invoice_number}</div></div></div>
        )} /></Panel></TabsContent>
      </Tabs>
      <ClientFormDialog open={edit} onOpenChange={setEdit} client={client} onSaved={setClient} />
      <AppointmentFormDialog open={sched} onOpenChange={setSched} defaultClientId={id} onSaved={load} />
    </div>
  );
}
